import type { APIBodyParamsPythonOnly, APIBodyParamsV2 } from '@highimpact-frontend/api-types';

import { DEFAULT_DECIMATION_POLICY } from './const';
import {
  decimationToApiParam,
  timeRangeToInterval,
} from './measureRequestHelpers';
import type { DecimationPolicy, TimeseriesCsvExportRequest } from './types';

// -- type definitions --

/** One measure source in a displayer. */
export interface MeasureSourceContext {
  measure_point_id: number;
  measure_type_id: number;
}

/** Column used when building a CSV export projection. */
export interface CsvExportQueryMapEntry {
  col_id: number;
  query_col_id: string;
}

/** Column metadata needed to detect the time column. */
export interface CsvExportColumnInfo {
  name: string;
  axis_type: string | number;
  requirements?: readonly number[];
}

/** Options that change how a measure request is built for CSV export. */
export interface CsvExportMeasureRequestOptions {
  selectedCurveIds: readonly string[];
  curveDisplayNames: Readonly<Record<string, string>>;
  curveToSourceIndex: Readonly<Record<string, number>>;
  decimation: DecimationPolicy;
}

/** Pipeline function shape used when rewriting the project step. */
export interface PipelineFunctionLike {
  function: string;
  parameters?: Record<string, string>;
}

// -- constants --

const DECIMATION_SAFE_PIPELINE_FUNCTIONS = new Set([
  'sort',
  'project',
  'apply_thresholds',
]);

// -- helper functions --

/** True when the pipeline is a sort/project timeseries shape safe to decimate. */
export function isDecimationAwareTimeseriesPipeline(
  functions: readonly PipelineFunctionLike[]
): boolean {
  if (functions.length === 0) {
    return false;
  }

  return functions.every((pipelineFunction) =>
    DECIMATION_SAFE_PIPELINE_FUNCTIONS.has(pipelineFunction.function)
  );
}

/** Histogram/bar plots keep horodate and skip decimation-aware project rewrites. */
function isHistogramMeasurePipeline(
  functions: readonly PipelineFunctionLike[]
): boolean {
  const projectFn = functions.find(
    (pipelineFunction) => pipelineFunction.function === 'project'
  );
  const parameters = projectFn?.parameters;
  if (!parameters || parameters.horodate !== 'horodate' || parameters.Temps) {
    return false;
  }

  return functions.some(
    (pipelineFunction) => pipelineFunction.function === 'sort'
  );
}

/** Check whether a column represents the time axis. */
export function isCsvExportTimeColumn(column: CsvExportColumnInfo): boolean {
  return String(column.axis_type) === 'x' && (column.requirements ?? []).length === 0;
}

/** Resolve the source indices selected in the export form. */
export function resolveSelectedSourceIndices(
  selectedCurveIds: readonly string[],
  curveToSourceIndex: Readonly<Record<string, number>>
): Set<number> {
  return new Set(
    selectedCurveIds
      .map((curveId) => curveToSourceIndex[curveId])
      .filter((sourceIndex): sourceIndex is number => sourceIndex !== undefined)
  );
}

/** Find the curve id linked to a measure source index. */
export function findCurveIdForSourceIndex(
  sourceIndex: number,
  selectedCurveIds: readonly string[],
  curveToSourceIndex: Readonly<Record<string, number>>
): string | undefined {
  const selectedMatch = selectedCurveIds.find(
    (curveId) => curveToSourceIndex[curveId] === sourceIndex
  );
  if (selectedMatch) {
    return selectedMatch;
  }

  return Object.entries(curveToSourceIndex).find(
    ([, index]) => index === sourceIndex
  )?.[0];
}

/** Keep only the measure sources selected in the export form. */
export function filterMeasuresBySourceIndices<T extends MeasureSourceContext>(
  measures: Readonly<Record<number, T>>,
  selectedSourceIndices: ReadonlySet<number>
): Record<number, T> {
  return Object.fromEntries(
    Object.entries(measures).filter(([sourceIndex]) =>
      selectedSourceIndices.has(Number(sourceIndex))
    )
  ) as Record<number, T>;
}

/** Sort measure entries by source index. */
export function sortMeasureEntries<T>(
  measures: Readonly<Record<number, T>>
): Array<[number, T]> {
  return Object.entries(measures)
    .map(([sourceIndex, measure]) => [Number(sourceIndex), measure] as [number, T])
    .sort(([leftIndex], [rightIndex]) => leftIndex - rightIndex);
}

/** Build context_index_to_name using curve display names. */
export function buildCsvContextIndexToName(
  measureEntries: ReadonlyArray<[number, MeasureSourceContext]>,
  selectedCurveIds: readonly string[],
  curveToSourceIndex: Readonly<Record<string, number>>,
  curveDisplayNames: Readonly<Record<string, string>>
): Record<number, string> {
  return measureEntries.reduce<Record<number, string>>(
    (result, [sourceIndex], contextIndex) => {
      const curveId = findCurveIdForSourceIndex(
        sourceIndex,
        selectedCurveIds,
        curveToSourceIndex
      );
      const displayName =
        (curveId ? curveDisplayNames[curveId] : undefined) ??
        curveId ??
        String(contextIndex);

      result[contextIndex] = displayName;
      return result;
    },
    {}
  );
}

/** Keep only the query map rows needed for the selected sources and the time column. */
export function filterQueryMapForCsvExport(
  queryMap: readonly CsvExportQueryMapEntry[],
  selectedSourceIndices: ReadonlySet<number>
): CsvExportQueryMapEntry[] {
  return queryMap.filter(({ query_col_id }) => {
    if (query_col_id === 'horodate') {
      return true;
    }

    const sourceIndex = Number(query_col_id);
    return (
      Number.isFinite(sourceIndex) && selectedSourceIndices.has(sourceIndex)
    );
  });
}

/** Resolve the output column name shown in the exported CSV file. */
export function resolveCsvExportColumnName(input: {
  column: CsvExportColumnInfo;
  queryColId: string;
  csvExportOptions: CsvExportMeasureRequestOptions;
}): string {
  const { column, queryColId, csvExportOptions } = input;

  if (queryColId === 'horodate' || isCsvExportTimeColumn(column)) {
    return column.name || 'Temps';
  }

  const sourceIndex = Number(queryColId);
  const curveId = findCurveIdForSourceIndex(
    sourceIndex,
    csvExportOptions.selectedCurveIds,
    csvExportOptions.curveToSourceIndex
  );

  if (!curveId) {
    return column.name;
  }

  return csvExportOptions.curveDisplayNames[curveId] ?? curveId;
}

/** Resolve the value reference used inside the project function for one curve. */
export function resolveCsvExportProjectValueReference(
  displayName: string,
  useDecimation: boolean
): string {
  return useDecimation ? `avg_${displayName}` : displayName;
}

/** Build project parameters from context_index_to_name values. */
export function buildPlotProjectParametersFromContextNames(
  contextIndexToName: Readonly<Record<string, string>>,
  useDecimation: boolean
): Record<string, string> {
  const projectParameters = Object.values(contextIndexToName).reduce<
    Record<string, string>
  >((result, name) => {
    result[name] = resolveCsvExportProjectValueReference(name, useDecimation);
    return result;
  }, {});

  projectParameters.Temps = 'horodate';
  return projectParameters;
}

/** Resolve context_index_to_name for project replacement at fetch time. */
export function resolveContextIndexToNameForProjection(
  body: APIBodyParamsPythonOnly
): Record<string, string> | undefined {
  const fromBody = body.context_index_to_name;
  if (fromBody && Object.keys(fromBody).length > 0) {
    return fromBody;
  }

  const contexts = body.contexts ?? [];
  if (contexts.length === 0) {
    return undefined;
  }

  return Object.fromEntries(
    contexts.map((_, index) => [String(index), String(index)])
  );
}

/** Removes apply_thresholds when no project step is present (backend requirement). */
export function stripApplyThresholdsWithoutProject(
  body: APIBodyParamsV2
): APIBodyParamsV2 {
  if (body.variant !== 'python_only') {
    return body;
  }

  const functions = body.pipeline?.functions ?? [];
  const hasThreshold = functions.some(
    (pipelineFunction) => pipelineFunction.function === 'apply_thresholds'
  );
  const hasProject = functions.some(
    (pipelineFunction) => pipelineFunction.function === 'project'
  );

  if (!hasThreshold || hasProject) {
    return body;
  }

  return {
    ...body,
    pipeline: {
      ...body.pipeline,
      functions: functions.filter(
        (pipelineFunction) => pipelineFunction.function !== 'apply_thresholds'
      ),
    },
  } as APIBodyParamsV2;
}

/** Patch decimation and project parameters on a python-only measure body. */
export function applyDecimationProjectParametersToApiBody(
  body: APIBodyParamsV2,
  decimation?: DecimationPolicy
): APIBodyParamsV2 {
  if (body.variant !== 'python_only') {
    return body;
  }

  const existingFunctions = (body.pipeline?.functions ??
    []) as PipelineFunctionLike[];

  if (
    !isDecimationAwareTimeseriesPipeline(existingFunctions) ||
    isHistogramMeasurePipeline(existingFunctions)
  ) {
    return stripApplyThresholdsWithoutProject(body);
  }

  const useDecimation = decimation?.enabled ?? false;
  const nextBody: APIBodyParamsV2 = {
    ...body,
    decimation: decimationToApiParam(decimation),
  } as APIBodyParamsV2;

  const contextIndexToName = resolveContextIndexToNameForProjection(body);
  if (!contextIndexToName) {
    return stripApplyThresholdsWithoutProject(nextBody);
  }

  const projectParameters = buildPlotProjectParametersFromContextNames(
    contextIndexToName,
    useDecimation
  );

  return stripApplyThresholdsWithoutProject({
    ...nextBody,
    pipeline: {
      ...body.pipeline,
      functions: replaceProjectFunctionInPipeline(
        existingFunctions,
        projectParameters
      ),
    },
  } as APIBodyParamsV2);
}

/** Build the project function parameters for a CSV export. */
export function buildCsvProjectParameters(input: {
  queryMap: readonly CsvExportQueryMapEntry[];
  columns: Readonly<Record<number, CsvExportColumnInfo>>;
  csvExportOptions: CsvExportMeasureRequestOptions;
}): Record<string, string> {
  const useDecimation = input.csvExportOptions.decimation.enabled;

  return input.queryMap.reduce<Record<string, string>>(
    (result, { col_id, query_col_id }) => {
      const column = input.columns[col_id];
      if (!column) {
        return result;
      }

      if (query_col_id === 'horodate' || isCsvExportTimeColumn(column)) {
        result[column.name || 'Temps'] = 'horodate';
        return result;
      }

      const displayName = resolveCsvExportColumnName({
        column,
        queryColId: query_col_id,
        csvExportOptions: input.csvExportOptions,
      });

      result[displayName] = resolveCsvExportProjectValueReference(
        displayName,
        useDecimation
      );
      return result;
    },
    {}
  );
}

/** Replace or append the project function in a pipeline, keeping apply_thresholds last. */
export function replaceProjectFunctionInPipeline(
  functions: readonly PipelineFunctionLike[],
  projectParameters: Record<string, string>
): PipelineFunctionLike[] {
  const thresholdFunctions = functions.filter(
    (pipelineFunction) => pipelineFunction.function === 'apply_thresholds'
  );
  const withoutProjectOrThresholds = functions.filter(
    (pipelineFunction) =>
      pipelineFunction.function !== 'project' &&
      pipelineFunction.function !== 'apply_thresholds'
  );

  return [
    ...withoutProjectOrThresholds,
    {
      function: 'project',
      parameters: projectParameters,
    },
    ...thresholdFunctions,
  ];
}

/** Build CSV export measure options from a transport request. */
export function resolveCsvExportMeasureOptions(
  request?: TimeseriesCsvExportRequest
): CsvExportMeasureRequestOptions | undefined {
  if (
    !request?.selectedCurveIds ||
    request.selectedCurveIds.length === 0 ||
    !request.curveDisplayNames ||
    !request.curveToSourceIndex
  ) {
    return undefined;
  }

  return {
    selectedCurveIds: request.selectedCurveIds,
    curveDisplayNames: request.curveDisplayNames,
    curveToSourceIndex: request.curveToSourceIndex,
    decimation: request.decimation ?? DEFAULT_DECIMATION_POLICY,
  };
}

/** Apply CSV export choices to an API body used by the session-cache transport. */
export function applyCsvExportOptionsToApiBody(
  body: APIBodyParamsV2,
  request: TimeseriesCsvExportRequest | undefined,
  timeRange: Parameters<typeof timeRangeToInterval>[0]
): APIBodyParamsV2 {
  const pipeline = body.variant === 'sql_and_python'
    ? body.python_pipeline 
    : body.variant === 'python_only'
    ? body.pipeline
    : undefined;
  if (!pipeline) {
    throw new Error('Invalid body variant');
  }
  const decimation = request?.decimation ?? DEFAULT_DECIMATION_POLICY;
  const csvExportOptions = resolveCsvExportMeasureOptions(request);

  const nextBody: APIBodyParamsV2 = {
    ...body,
    time_range: timeRangeToInterval(timeRange),
    decimation: decimationToApiParam(decimation),
  } as APIBodyParamsPythonOnly;

  if (!csvExportOptions) {
    return nextBody;
  }

  const selectedSourceIndices = resolveSelectedSourceIndices(
    csvExportOptions.selectedCurveIds,
    csvExportOptions.curveToSourceIndex
  );
  const contexts = (body.contexts ?? []) as MeasureSourceContext[];
  const filteredContexts = contexts.filter((_, sourceIndex) =>
    selectedSourceIndices.has(sourceIndex)
  );
  const measureEntries = filteredContexts.map(
    (measure, sourceIndex) => [sourceIndex, measure] as [number, MeasureSourceContext]
  );

  const contextIndexToName = buildCsvContextIndexToName(
    measureEntries,
    csvExportOptions.selectedCurveIds,
    csvExportOptions.curveToSourceIndex,
    csvExportOptions.curveDisplayNames
  );

  const projectParameters = buildPlotProjectParametersFromContextNames(
    contextIndexToName,
    csvExportOptions.decimation.enabled
  );

  const existingFunctions = (pipeline.functions ??
    []) as PipelineFunctionLike[];

  return {
    ...nextBody,
    contexts: filteredContexts,
    context_index_to_name: contextIndexToName,
    pipeline: {
      ...pipeline,
      functions: replaceProjectFunctionInPipeline(
        existingFunctions,
        projectParameters
      ),
    },
  } as APIBodyParamsV2;
}
